import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { toast } from 'react-toastify';
import moment from 'moment';

import api from '../../../services';

const useStyles = makeStyles((theme) => ({
  panel: { maxWidth: 640, paddingTop: theme.spacing(1) },
  row: {
    padding: theme.spacing(2),
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

export default function SecurityTab() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/sessions');
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (id) => {
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success('Signed out that device');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not revoke');
    }
  };

  const revokeOthers = async () => {
    try {
      await api.post('/auth/sessions/revoke-others');
      toast.success('Other devices signed out');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not sign out other devices');
    }
  };

  return (
    <div className={classes.panel}>
      <Typography variant="h6" gutterBottom>
        Security
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Password changes use{' '}
        <Link to="/forgot-password">Forgot password</Link> from the sign-in page. Below are browsers that remember your
        account (refresh tokens).
      </Typography>
      <Box mb={2}>
        <Button variant="outlined" color="primary" onClick={revokeOthers} disabled={sessions.length <= 1}>
          Sign out all other devices
        </Button>
      </Box>
      {loading ? (
        <CircularProgress size={28} />
      ) : sessions.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No active sessions.
        </Typography>
      ) : (
        sessions.map((s) => (
          <Paper key={s.id} className={classes.row} elevation={0}>
            <div style={{ minWidth: 0 }}>
              <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" noWrap style={{ maxWidth: '100%' }}>
                  {s.userAgent || 'Unknown browser'}
                </Typography>
                {s.current ? <Chip size="small" label="This device" color="primary" /> : null}
                {s.rememberMe ? <Chip size="small" label="Remembered" variant="outlined" /> : null}
              </Box>
              <Typography variant="caption" color="textSecondary" display="block">
                IP: {s.ip || '—'} · Last active {s.lastSeenAt ? moment(s.lastSeenAt).fromNow() : '—'}
              </Typography>
            </div>
            {!s.current ? (
              <Button size="small" onClick={() => revoke(s.id)}>
                Sign out
              </Button>
            ) : null}
          </Paper>
        ))
      )}
    </div>
  );
}
